import React from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { Heart, MapPin } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { localized } from "@/i18n";
import { CATEGORIES, TAGS, priceSymbol } from "@/constants";

export default function PlaceCard({ place, compact = false }) {
  const navigate = useNavigate();
  const { lang, t, isFavorite, toggleFavorite } = useApp();
  const cat = CATEGORIES.find((c) => c.id === place.category);
  const CatIcon = cat ? Icons[cat.icon] : Icons.MapPin;
  const fav = isFavorite(place.id);
  const img = place.images?.[0];

  return (
    <div
      data-testid={`place-card-${place.id}`}
      onClick={() => navigate(`/place/${place.id}`)}
      className="group cursor-pointer rounded-2xl overflow-hidden bg-white border border-border shadow-sm hover:shadow-xl transition-shadow duration-300"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-sand">
        {img ? (
          <img
            src={img}
            alt={place.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-terracotta-light to-sand">
            <CatIcon className="h-10 w-10 text-terracotta/50" />
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/50 to-transparent" />
        <button
          data-testid={`fav-toggle-${place.id}`}
          onClick={(e) => { e.stopPropagation(); toggleFavorite(place.id); }}
          aria-label={fav ? t("remove_fav") : t("add_fav")}
          className="absolute top-2.5 right-2.5 h-9 w-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md active:scale-90 transition-transform"
        >
          <Heart className={`h-[18px] w-[18px] ${fav ? "fill-terracotta text-terracotta" : "text-ink/60"}`} />
        </button>
        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold text-white shadow"
            style={{ backgroundColor: cat?.color || "#0F4C5C" }}
          >
            <CatIcon className="h-3 w-3" /> {localized(cat?.label, lang)}
          </span>
        </div>
        {place.team_pick && (
          <span className="absolute bottom-2.5 left-2.5 rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            {t("team_picks_title")}
          </span>
        )}
      </div>
      <div className="p-3.5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading font-bold text-[15px] leading-tight text-ink line-clamp-2">{place.name}</h3>
          <span className="shrink-0 font-mono text-sm font-bold text-emerald-700">{priceSymbol(place.price_level)}</span>
        </div>
        <div className="mt-1.5 flex items-center gap-2 text-xs text-ink-muted">
          <MapPin className="h-3.5 w-3.5" />
          <span className="truncate">{place.neighborhood}</span>
          {place.status === "temporarily_closed" ? (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-red-300 px-2 py-0.5 text-[10px] font-semibold text-red-600">{t("closed_temp")}</span>
          ) : place.open_now === true ? (
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />{t("open")}</span>
          ) : place.open_now === false ? (
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold text-red-500"><span className="h-1.5 w-1.5 rounded-full bg-red-400" />{t("closed")}</span>
          ) : null}
          {typeof place.distance_km === "number" && (
            <span className="ml-auto text-[11px] font-semibold text-sea">{place.distance_km} km</span>
          )}
        </div>
        {!compact && (
          <p className="mt-2 text-xs text-ink-muted line-clamp-2 leading-relaxed">{localized(place.summary, lang)}</p>
        )}
        <div className="mt-2.5 flex flex-wrap gap-1.5">
          {(place.tags || []).slice(0, 3).map((tag) => (
            <span key={tag} className="rounded-full bg-sand px-2 py-0.5 text-[10px] font-medium text-ink/70">
              {localized(TAGS[tag], lang) || tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
